// src/pages/DriverDetails.js - COMPLETE UPDATED VERSION
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function DriverDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [recentRides, setRecentRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // ✅ ADD THIS - Tab state
  const [activeTab, setActiveTab] = useState('info');
  
  // Block/Suspend/Terminate states
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [terminateReason, setTerminateReason] = useState('');
  const [driverStatus, setDriverStatus] = useState({
    is_blocked: false,
    is_suspended: false,
    is_terminated: false,
    account_status: 'active'
  });
  
  // Image Popup state
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageTitle, setSelectedImageTitle] = useState('');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  const getAuthToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  useEffect(() => {
    fetchDriverDetails();
  }, [id]);

  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      
      const token = getAuthToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch driver details
      const driverRes = await axios.get(
        `https://boda-backend-wuea.onrender.com/api/admin/driver/${id}`,
        { headers }
      );
      
      console.log('Driver data received:', driverRes.data);
      
      if (driverRes.data && driverRes.data.driver) {
        setDriver(driverRes.data.driver);
        setDocuments(driverRes.data.documents || []);
        setWallet(driverRes.data.wallet || { balance: 0, total_earned: 0 });
        setRecentRides(driverRes.data.recent_rides || []);
      }
      
      // Try to fetch status, but don't fail if it doesn't exist
      try {
        const statusRes = await axios.get(
          `https://boda-backend-wuea.onrender.com/api/admin/driver/status/${id}`,
          { headers }
        );
        if (statusRes.data && statusRes.data.status) {
          setDriverStatus(statusRes.data.status);
        }
      } catch (statusError) {
        console.log('Status endpoint not available, using default');
      }
      
    } catch (error) {
      console.error('Error fetching driver:', error);
      if (error.response?.status === 401) {
        alert('Please login as admin first');
      } else if (error.response?.status === 404) {
        alert('Driver not found');
      } else {
        alert('Failed to load driver details: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const approveDriver = async () => {
    if (!window.confirm('Approve this driver?')) return;
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/approve-driver/${id}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Driver approved successfully!');
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to approve driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const rejectDriver = async () => {
    if (!rejectReason.trim()) {
      alert('Please enter a rejection reason');
      return;
    }
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/reject-driver/${id}`,
        { reason: rejectReason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Driver rejected');
      setShowRejectModal(false);
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to reject driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const blockDriver = async () => {
    if (!blockReason.trim()) {
      alert('Please enter a reason for blocking');
      return;
    }
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/driver/block/${id}`,
        { reason: blockReason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Driver blocked successfully');
      setShowBlockModal(false);
      setBlockReason('');
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to block driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const suspendDriver = async () => {
    if (!suspendReason.trim()) {
      alert('Please enter a reason for suspension');
      return;
    }
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/driver/suspend/${id}`,
        { reason: suspendReason, duration_days: suspendDays },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(`Driver suspended for ${suspendDays} days`);
      setShowSuspendModal(false);
      setSuspendReason('');
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to suspend driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const terminateDriver = async () => {
    if (!terminateReason.trim()) {
      alert('Please enter a reason for termination');
      return;
    }
    
    if (!window.confirm('⚠️ This action is permanent. Are you sure you want to terminate this driver?')) return;
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/driver/terminate/${id}`,
        { reason: terminateReason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Driver terminated permanently');
      setShowTerminateModal(false);
      setTerminateReason('');
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to terminate driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const restoreDriver = async () => {
    if (!window.confirm('Restore this driver account?')) return;
    
    setApproving(true);
    try {
      const token = getAuthToken();
      await axios.put(
        `https://boda-backend-wuea.onrender.com/api/admin/driver/restore/${id}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert('Driver restored successfully');
      fetchDriverDetails();
    } catch (error) {
      alert('Failed to restore driver: ' + (error.response?.data?.error || error.message));
    } finally {
      setApproving(false);
    }
  };

  const openDocument = (doc) => {
    const docUrl = doc.document_url;
    
    if (!docUrl) {
      alert('No document URL found');
      return;
    }

    if (docUrl.startsWith('/9j/') || docUrl.startsWith('iVBORw0KGgo')) {
      const imageSrc = docUrl.startsWith('/9j/') 
        ? `data:image/jpeg;base64,${docUrl}`
        : `data:image/png;base64,${docUrl}`;
      
      setSelectedImage(imageSrc);
      setSelectedImageTitle(doc.document_type?.replace('_', ' ') || 'Document');
      setShowImagePopup(true);
      return;
    }

    if (docUrl.startsWith('http') || docUrl.startsWith('data:')) {
      window.open(docUrl, '_blank');
      return;
    }

    alert('Unable to display document. Format not supported.');
  };

  const closePopup = () => {
    setShowImagePopup(false);
    setSelectedImage('');
    setSelectedImageTitle('');
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: '#f59e0b', text: 'Pending Approval' },
      approved: { color: '#10b981', text: 'Approved' },
      rejected: { color: '#ef4444', text: 'Rejected' },
      not_submitted: { color: '#6b7280', text: 'Not Submitted' },
      blocked: { color: '#ef4444', text: '🚫 Blocked' },
      suspended: { color: '#f59e0b', text: '⏸️ Suspended' },
      terminated: { color: '#dc2626', text: '⚠️ Terminated' },
      active: { color: '#10b981', text: '✅ Active' }
    };
    const config = statusConfig[status] || { color: '#6b7280', text: status };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: config.color + '20',
        color: config.color,
      }}>
        {config.text}
      </span>
    );
  };

  const getAccountStatus = () => {
    if (!driverStatus) return 'active';
    if (driverStatus.is_terminated) return 'terminated';
    if (driverStatus.is_suspended) return 'suspended';
    if (driverStatus.is_blocked) return 'blocked';
    return 'active';
  };

  const isImageDocument = (docUrl) => {
    return docUrl && (docUrl.startsWith('/9j/') || docUrl.startsWith('iVBORw0KGgo'));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading driver details...</div>
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', padding: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
        <div style={{ fontSize: '18px', color: '#64748b', textAlign: 'center' }}>Driver not found</div>
        <button
          onClick={() => navigate('/drivers')}
          style={{
            marginTop: '20px',
            padding: '10px 24px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Back to Drivers
        </button>
        <button
          onClick={fetchDriverDetails}
          style={{
            marginTop: '10px',
            padding: '10px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  const accountStatus = getAccountStatus();

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header with actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/drivers')}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#64748b'
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0 }}>Driver Profile</h1>
        {driver.verification_status === 'pending' && (
          <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
            <button
              onClick={approveDriver}
              disabled={approving}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '8px 24px',
                borderRadius: '8px',
                cursor: approving ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: approving ? 0.6 : 1
              }}
            >
              ✓ Approve
            </button>
            <button
              onClick={() => setShowRejectModal(true)}
              disabled={approving}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '8px 24px',
                borderRadius: '8px',
                cursor: approving ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: approving ? 0.6 : 1
              }}
            >
              ✗ Reject
            </button>
          </div>
        )}
        
        {/* Account Status Actions */}
        {accountStatus !== 'terminated' && (
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap' }}>
            {accountStatus === 'active' && (
              <>
                <button
                  onClick={() => setShowBlockModal(true)}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px',
                  }}
                >
                  🚫 Block
                </button>
                <button
                  onClick={() => setShowSuspendModal(true)}
                  style={{
                    backgroundColor: '#f97316',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px',
                  }}
                >
                  ⏸️ Suspend
                </button>
                <button
                  onClick={() => setShowTerminateModal(true)}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '13px',
                  }}
                >
                  ⚠️ Terminate
                </button>
              </>
            )}
            {(accountStatus === 'blocked' || accountStatus === 'suspended') && (
              <button
                onClick={restoreDriver}
                disabled={approving}
                style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px',
                }}
              >
                🔄 Restore Account
              </button>
            )}
          </div>
        )}
      </div>

      {/* ✅ TABS */}
      <div style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '20px', 
        borderBottom: '1px solid #e2e8f0' 
      }}>
        <button
          onClick={() => setActiveTab('info')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'info' ? '#f97316' : 'transparent',
            color: activeTab === 'info' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'info' ? '600' : '400',
          }}
        >
          📋 Info
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'performance' ? '#f97316' : 'transparent',
            color: activeTab === 'performance' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'performance' ? '600' : '400',
          }}
        >
          📊 Performance
        </button>
        <button
          onClick={() => setActiveTab('rides')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'rides' ? '#f97316' : 'transparent',
            color: activeTab === 'rides' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'rides' ? '600' : '400',
          }}
        >
          🚗 Rides
        </button>
      </div>

      {/* Status Banner */}
      {accountStatus !== 'active' && (
        <div style={{
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          backgroundColor: accountStatus === 'blocked' ? '#fef3c7' :
                          accountStatus === 'suspended' ? '#fef3c7' :
                          accountStatus === 'terminated' ? '#fee2e2' : '#f1f5f9',
          border: `1px solid ${accountStatus === 'blocked' ? '#f59e0b' :
                                  accountStatus === 'suspended' ? '#f59e0b' :
                                  accountStatus === 'terminated' ? '#dc2626' : '#e2e8f0'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '24px' }}>
              {accountStatus === 'blocked' ? '🚫' :
               accountStatus === 'suspended' ? '⏸️' :
               accountStatus === 'terminated' ? '⚠️' : 'ℹ️'}
            </span>
            <div>
              <strong style={{ textTransform: 'capitalize' }}>{accountStatus}</strong>
              {driverStatus?.blocked_reason && <span> - {driverStatus.blocked_reason}</span>}
              {driverStatus?.suspended_reason && <span> - {driverStatus.suspended_reason}</span>}
              {driverStatus?.terminated_reason && <span> - {driverStatus.terminated_reason}</span>}
              {driverStatus?.suspended_until && (
                <span style={{ color: '#64748b', fontSize: '13px', marginLeft: '8px' }}>
                  (Until: {new Date(driverStatus.suspended_until).toLocaleDateString()})
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* TAB CONTENT - INFO */}
      {/* ============================================================ */}
      {activeTab === 'info' && (
        <>
          {/* Driver Info Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0' }}>{driver.full_name || 'Unknown Driver'}</h2>
                <p style={{ color: '#64748b', margin: '0 0 4px 0' }}>📞 {driver.phone_number || 'No phone'}</p>
                <p style={{ color: '#64748b', margin: '0 0 4px 0' }}>✉️ {driver.email || 'No email provided'}</p>
                <p style={{ color: '#64748b', margin: 0 }}>📅 Joined: {driver.created_at ? new Date(driver.created_at).toLocaleDateString() : 'Unknown'}</p>
              </div>
              <div>
                {getStatusBadge(driver.verification_status || 'pending')}
              </div>
            </div>
          </div>

          {/* Vehicle & Wallet */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {/* Vehicle Information */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 16px 0' }}>🚗 Vehicle Information</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <strong>Vehicle Type:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>
                    {driver.vehicle_type === 'boda' ? '🛵 Boda Boda' : 
                     driver.vehicle_type === 'car' ? '🚗 Car' : 
                     driver.vehicle_type === 'boda_electric' ? '⚡ Electric Boda' : 
                     driver.vehicle_type === 'car_xl' ? '🚙 Car XL' : 'Not specified'}
                  </p>
                </div>
                <div>
                  <strong>Registration Number:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>{driver.vehicle_registration || 'Not provided'}</p>
                </div>
                <div>
                  <strong>License Number:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>{driver.license_number || 'Not provided'}</p>
                </div>
              </div>
            </div>

            {/* Wallet & Stats */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 16px 0' }}>💰 Wallet & Stats</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <strong>Balance:</strong>
                  <p style={{ margin: '4px 0 0 0', color: (wallet?.balance || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                    UGX {(wallet?.balance || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <strong>Total Earned:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>UGX {(wallet?.total_earned || 0).toLocaleString()}</p>
                </div>
                <div>
                  <strong>Total Rides:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>{driver.total_rides || 0}</p>
                </div>
                <div>
                  <strong>Rating:</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#64748b' }}>
                    {driver.average_rating ? `${driver.average_rating} ⭐` : 'No ratings yet'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 16px 0' }}>📄 Uploaded Documents</h3>
            {documents.length === 0 ? (
              <p style={{ color: '#64748b', textAlign: 'center' }}>No documents uploaded yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {documents.map((doc, index) => {
                  const isImage = isImageDocument(doc.document_url);
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: isImage ? '#dcfce7' : '#e2e8f0',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px'
                        }}>
                          {isImage ? '🖼️' : '📄'}
                        </div>
                        <div>
                          <strong style={{ textTransform: 'capitalize' }}>
                            {doc.document_type?.replace('_', ' ') || 'Document'}:
                          </strong>
                          <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '12px' }}>
                            Uploaded {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Unknown'}
                          </span>
                          {isImage && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#10b981' }}>
                              ✓ Image
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => openDocument(doc)}
                        style={{
                          padding: '6px 16px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        👁️ View
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ============================================================ */}
      {/* TAB CONTENT - PERFORMANCE */}
      {/* ============================================================ */}
      {activeTab === 'performance' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px',
        }}>
          <h3 style={{ marginTop: 0 }}>📊 Driver Performance</h3>
          
          {/* Performance Stats */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '16px',
            marginTop: '16px',
            marginBottom: '20px',
          }}>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rides</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f97316' }}>
                {recentRides.length}
              </div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Completed</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                {recentRides.filter(r => r.status === 'completed').length}
              </div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Rating</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                {driver?.average_rating ? `${driver.average_rating} ⭐` : '—'}
              </div>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Earnings</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
                UGX {wallet?.total_earned?.toLocaleString() || 0}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(`/driver-performance?driver=${id}`)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              View Full Performance Dashboard →
            </button>
            <button
              onClick={() => {
                // Refresh performance data
                fetchDriverDetails();
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* TAB CONTENT - RIDES */}
      {/* ============================================================ */}
      {activeTab === 'rides' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px',
        }}>
          <h3 style={{ marginTop: 0 }}>🚗 All Rides</h3>
          
          {recentRides.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>No rides yet</p>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Ride ID</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Fare</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Rating</th>
                    <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRides.map((ride) => (
                    <tr key={ride.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>#{ride.id}</td>
                      <td style={{ padding: '12px', fontWeight: 'bold', color: '#f97316' }}>
                        UGX {ride.total_fare?.toLocaleString() || 0}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          backgroundColor: ride.status === 'completed' ? '#dcfce7' : 
                                         ride.status === 'cancelled' ? '#fee2e2' : '#fef3c7',
                          color: ride.status === 'completed' ? '#166534' : 
                                 ride.status === 'cancelled' ? '#991b1b' : '#92400e',
                        }}>
                          {ride.status || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {ride.rating ? `⭐ ${ride.rating}` : '—'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                        {ride.created_at ? new Date(ride.created_at).toLocaleDateString() : 'Unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {recentRides.length > 0 && (
            <div style={{ 
              padding: '12px', 
              color: '#64748b', 
              fontSize: '13px',
              borderTop: '1px solid #e2e8f0',
              marginTop: '12px',
            }}>
              Showing {recentRides.length} recent rides
              <button
                onClick={() => navigate(`/rides?driver=${id}`)}
                style={{
                  marginLeft: '16px',
                  padding: '4px 12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                View All →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* MODALS */}
      {/* ============================================================ */}

      {/* Block Modal */}
      {showBlockModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f59e0b' }}>🚫 Block Driver</h3>
            <p>This driver will be blocked from going online.</p>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Reason for blocking (required)"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBlockModal(false); setBlockReason(''); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={blockDriver}
                disabled={approving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  opacity: approving ? 0.6 : 1
                }}
              >
                Block Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f97316' }}>⏸️ Suspend Driver</h3>
            <p>This driver will be temporarily suspended.</p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Suspension Duration (days)</label>
              <select
                value={suspendDays}
                onChange={(e) => setSuspendDays(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
              </select>
            </div>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Reason for suspension (required)"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={suspendDriver}
                disabled={approving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  opacity: approving ? 0.6 : 1
                }}
              >
                Suspend Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terminate Modal */}
      {showTerminateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#dc2626' }}>⚠️ Terminate Driver</h3>
            <p style={{ color: '#dc2626', fontWeight: 'bold' }}>⚠️ This action is PERMANENT and cannot be undone!</p>
            <textarea
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
              placeholder="Reason for termination (required)"
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowTerminateModal(false); setTerminateReason(''); }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={terminateDriver}
                disabled={approving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  opacity: approving ? 0.6 : 1
                }}
              >
                Terminate Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#ef4444' }}>Reject Driver</h3>
            <p>Please provide a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Invalid license, Documents not clear, etc."
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '20px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e2e8f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={rejectDriver}
                disabled={approving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: approving ? 'not-allowed' : 'pointer',
                  opacity: approving ? 0.6 : 1
                }}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Popup */}
      {showImagePopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={closePopup}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc'
            }}>
              <span style={{
                fontWeight: 'bold',
                fontSize: '16px',
                color: '#1e293b'
              }}>
                {selectedImageTitle}
              </span>
              <button
                onClick={closePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ✕
              </button>
            </div>
            
            <div style={{
              padding: '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
              minWidth: '300px',
              maxHeight: '70vh',
              overflow: 'auto',
              backgroundColor: '#f1f5f9'
            }}>
              <img 
                src={selectedImage} 
                alt={selectedImageTitle}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  borderRadius: '4px',
                  objectFit: 'contain',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const parent = e.target.parentNode;
                  const errorDiv = document.createElement('div');
                  errorDiv.style.cssText = 'color: #ef4444; padding: 40px; text-align: center; font-size: 16px;';
                  errorDiv.textContent = '⚠️ Could not load image. The file may be corrupted.';
                  parent.appendChild(errorDiv);
                }}
              />
            </div>
            
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              textAlign: 'center',
              fontSize: '12px',
              color: '#94a3b8',
              backgroundColor: '#f8fafc'
            }}>
              Click outside or press ESC to close
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DriverDetails;